import type { Codec, EncodingMode, Resolution } from '../api/sessions';

const ENCODING_KEY = 'relay.encoding';
const RESOLUTION_KEY = 'relay.resolution';
const CODEC_KEY = 'relay.codec';
const FPS_KEY = 'relay.fps';
const JPEG_QUALITY_KEY = 'relay.jpegQuality';
const DEFAULT_ENCODING: EncodingMode = 'full';
const DEFAULT_RESOLUTION: Resolution = 'native';
const DEFAULT_CODEC: Codec = 'h264';
const DEFAULT_FPS = 20;
const DEFAULT_JPEG_QUALITY = 75;
const FPS_CHOICES = [10, 15, 20] as const;        // 20 is the DXGI+sharp cap
const JPEG_QUALITY_CHOICES = [40, 60, 75, 85] as const; // low -> high

export function readEncodingPref(): EncodingMode {
  try {
    const raw = localStorage.getItem(ENCODING_KEY);
    if (raw === 'full' || raw === 'skip-static') return raw;
  } catch { /* ignore */ }
  return DEFAULT_ENCODING;
}

export function writeEncodingPref(mode: EncodingMode): void {
  try { localStorage.setItem(ENCODING_KEY, mode); } catch { /* ignore */ }
}

export function readResolutionPref(): Resolution {
  try {
    const raw = localStorage.getItem(RESOLUTION_KEY);
    if (raw === 'native' || raw === '720p' || raw === '540p' || raw === '360p') return raw;
  } catch { /* ignore */ }
  return DEFAULT_RESOLUTION;
}

export function writeResolutionPref(res: Resolution): void {
  try { localStorage.setItem(RESOLUTION_KEY, res); } catch { /* ignore */ }
}

export function readCodecPref(): Codec {
  try {
    const raw = localStorage.getItem(CODEC_KEY);
    if (raw === 'jpeg' || raw === 'h264') return raw;
  } catch { /* ignore */ }
  return DEFAULT_CODEC;
}

export function writeCodecPref(codec: Codec): void {
  try { localStorage.setItem(CODEC_KEY, codec); } catch { /* ignore */ }
}

export function readFpsPref(): number {
  try {
    const raw = localStorage.getItem(FPS_KEY);
    if (raw) {
      const n = Number.parseInt(raw, 10);
      if ((FPS_CHOICES as readonly number[]).includes(n)) return n;
    }
  } catch { /* ignore */ }
  return DEFAULT_FPS;
}

export function writeFpsPref(fps: number): void {
  try { localStorage.setItem(FPS_KEY, String(fps)); } catch { /* ignore */ }
}

export function readJpegQualityPref(): number {
  try {
    const raw = localStorage.getItem(JPEG_QUALITY_KEY);
    if (raw) {
      const n = Number.parseInt(raw, 10);
      if ((JPEG_QUALITY_CHOICES as readonly number[]).includes(n)) return n;
    }
  } catch { /* ignore */ }
  return DEFAULT_JPEG_QUALITY;
}

export function writeJpegQualityPref(q: number): void {
  try { localStorage.setItem(JPEG_QUALITY_KEY, String(q)); } catch { /* ignore */ }
}

export { FPS_CHOICES, JPEG_QUALITY_CHOICES };

const STAGE_WIDTH_KEY = 'relay.stageWidth';

export function readStageWidthPref(): number | null {
  try {
    const raw = localStorage.getItem(STAGE_WIDTH_KEY);
    if (raw === null) return null;
    const n = Number.parseInt(raw, 10);
    return Number.isFinite(n) && n >= 320 ? n : null;
  } catch { /* ignore */ }
  return null;
}

export function writeStageWidthPref(value: number | null): void {
  try {
    if (value === null) localStorage.removeItem(STAGE_WIDTH_KEY);
    else localStorage.setItem(STAGE_WIDTH_KEY, String(Math.round(value)));
  } catch { /* ignore */ }
}
